import React, { useEffect, useMemo, useState } from "react";
import { confirmDialog } from "../../utils/toast";

const PAGE_SIZE = 9;

function normalizeRole(roleCode) {
  return String(roleCode || "USER").trim().toUpperCase();
}

function maskPhone(phoneMid, phoneLast) {
  const mid = String(phoneMid || "").trim();
  const last = String(phoneLast || "").trim();
  if (last.length === 4) return `010-****-${last}`;
  if (mid.length === 4 || last.length > 0) return `010-****-${last || "****"}`;
  return "-";
}

function normalizeUser(row) {
  return {
    userIdx: row.UserIdx,
    userId: row.UserID,
    userName: row.UserName,
    userPathKey: row.UserPathKey,
    companyName: row.CompanyName || "",
    // 가입 이후 수정 대상. 여기서 떨구면 API가 내려줘도 화면까지 오지 않는다
    // (BranchName 은 원래 API가 주고 있었는데 이 함수가 버리고 있었다).
    branchName: row.BranchName || "",
    teamName: row.TeamName || "",
    vehicleNo: row.VehicleNo || "",
    phoneMid: row.PhoneMid || "",
    phoneLast: row.PhoneLast || "",
    roleCode: normalizeRole(row.RoleCode || "USER"),
    approvalStatus: Number(row.ApprovalStatus || 0),
    createdAt: row.CreatedAt || "",
    canMessages: !!row.CanMessages,
  };
}

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [savingUserIdx, setSavingUserIdx] = useState(null);
  const [roleSavingUserIdx, setRoleSavingUserIdx] = useState(null);
  const [me, setMe] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [companyFilter, setCompanyFilter] = useState(""); // "" = 전체
  const [selectedIdx, setSelectedIdx] = useState(null);

  // 민트워치 권한 임시값 (userIdx -> bool)
  const [watchDraft, setWatchDraft] = useState({});

  // 비밀번호 초기화 ? 발급된 임시 비밀번호는 화면에만 잠시 남기고 저장하지 않는다.
  const [pwResetBusy, setPwResetBusy] = useState(null);
  const [issuedPassword, setIssuedPassword] = useState(null);

  // 프로필(지점·팀·차량번호) 임시값 (userIdx -> {branchName, teamName, vehicleNo}).
  // 가입할 때 한 번 받고 끝이라 차량 교체·팀 이동·오타를 고칠 방법이 없었다.
  const [profileDraft, setProfileDraft] = useState({});
  const [profileBusy, setProfileBusy] = useState(null);

  // 지점·팀은 자유 입력이 아니라 등록된 것 중에서 고르게 한다.
  // 오타가 들어가면 가입 드롭다운과 어긋나 그 계정이 목록에서 고를 수 없는 상태가 된다.
  const [branchOptions, setBranchOptions] = useState([]);
  const [teamOptions, setTeamOptions] = useState([]);

  const setProfileField = (userIdx, field, value) => {
    setProfileDraft((prev) => ({
      ...prev,
      [userIdx]: { ...(prev[userIdx] || {}), [field]: value },
    }));
  };

  // 지점 목록 — /api/admin/branches 는 슈퍼어드민 전용이라 일반 관리자에겐 403 이 난다.
  // 실패해도 화면은 그대로 쓰게 두고, 그 경우 지점은 표시만 하고 고치지 못하게 한다
  // (서버도 지점 변경을 회사 전체 관리자로 제한한다).
  const loadBranchOptions = async () => {
    try {
      const res = await fetch("/api/admin/branches", { credentials: "include" });
      const data = await res.json();
      setBranchOptions(
        res.ok && data?.success && Array.isArray(data.branches) ? data.branches : []
      );
    } catch {
      setBranchOptions([]);
    }
  };

  // 팀 목록 — 가입 화면이 쓰는 공개 조회를 그대로 재사용한다.
  // 팀은 <회사 + 지점> 아래 단위라 지점이 정해져야 조회된다.
  const loadTeamOptions = async (companyName, branchName) => {
    const c = String(companyName || "").trim();
    const b = String(branchName || "").trim();

    if (!c || !b) {
      setTeamOptions([]);
      return;
    }

    try {
      const res = await fetch(
        `/api/teams?company=${encodeURIComponent(c)}&branch=${encodeURIComponent(b)}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setTeamOptions(data?.success && Array.isArray(data.teams) ? data.teams : []);
    } catch {
      setTeamOptions([]);
    }
  };

  const loadMe = async () => {
    try {
      const res = await fetch("/api/me", { credentials: "include" });
      const data = await res.json();
      setMe(res.ok && data?.loggedIn && data?.user ? data.user : null);
    } catch (err) {
      setMe(null);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch("/api/admin/users", { credentials: "include" });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setMsg(data.error || data.message || "사용자 목록 조회 실패");
        setUsers([]);
        return;
      }
      const grouped = new Map();
      (data.users || []).forEach((row) => {
        if (!grouped.has(row.UserIdx)) grouped.set(row.UserIdx, normalizeUser(row));
      });
      const list = Array.from(grouped.values());
      setUsers(list);
      const nextDraft = {};
      list.forEach((u) => {
        nextDraft[u.userIdx] = u.canMessages;
      });
      setWatchDraft(nextDraft);

      // 프로필 임시값도 같이 채운다. 저장 전까지는 화면에서만 바뀐다.
      const nextProfile = {};
      list.forEach((u) => {
        nextProfile[u.userIdx] = {
          branchName: u.branchName || "",
          teamName: u.teamName || "",
          vehicleNo: u.vehicleNo || "",
        };
      });
      setProfileDraft(nextProfile);
    } catch (err) {
      setMsg("사용자 목록을 불러오는 중 오류가 발생했습니다.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMe();
    loadUsers();
    loadBranchOptions();
  }, []);

  // 프로필(지점·팀·차량번호) 저장.
  // 서버가 지점·팀을 TB_BRANCH·TB_TEAM 에 있는 값인지 검사하고,
  // 지점 변경은 회사 전체 관리자만 되도록 막는다. 실패 메시지를 그대로 보여준다.
  const handleSaveProfile = async (user) => {
    const draft = profileDraft[user.userIdx] || {};

    try {
      setProfileBusy(user.userIdx);

      const res = await fetch("/api/admin/user/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userIdx: user.userIdx,
          branchName: draft.branchName || "",
          teamName: draft.teamName || "",
          vehicleNo: draft.vehicleNo || "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        alert(data?.message || data?.error || "저장하지 못했습니다.");
        return;
      }

      alert("저장했습니다.");
      loadUsers();
    } catch (err) {
      alert("저장 중 오류가 발생했습니다.");
    } finally {
      setProfileBusy(null);
    }
  };

  const myRole = normalizeRole(me?.roleCode);
  const amISuperAdmin = myRole === "SUPER_ADMIN";
  const amIAdmin = myRole === "ADMIN";

  const sortedUsers = useMemo(() => {
    const rank = (role) => {
      const r = normalizeRole(role);
      if (r === "SUPER_ADMIN") return 0;
      if (r === "ADMIN") return 1;
      return 2;
    };
    return [...users].sort((a, b) => {
      const d = rank(a.roleCode) - rank(b.roleCode);
      return d !== 0 ? d : Number(a.userIdx) - Number(b.userIdx);
    });
  }, [users]);

  /*
    회사별로 묶어서 본다.

    삼우에프앤지 계정이 400개라, 그 뒤에 만들어진 다른 회사 계정이 목록에 파묻혀
    하나하나 찾아야 했다. 회사를 먼저 고르고 그 안에서 찾는 방식으로 바꾼다.
    계정 수가 많은 회사부터 위에 둔다.
  */
  const companyGroups = useMemo(() => {
    const m = new Map();
    sortedUsers.forEach((u) => {
      const name = String(u.companyName || "").trim() || "(회사 미지정)";
      m.set(name, (m.get(name) || 0) + 1);
    });
    return [...m.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [sortedUsers]);

  const filteredUsers = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();

    const base = !companyFilter
      ? sortedUsers
      : sortedUsers.filter(
          (u) =>
            (String(u.companyName || "").trim() || "(회사 미지정)") === companyFilter
        );

    if (!q) return base;
    return base.filter((u) => {
      const hay = [u.userId, u.userName, u.companyName, u.roleCode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [sortedUsers, search, companyFilter]);

  const pageCount = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const pagedUsers = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), pageCount);
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, page, pageCount]);

  const selectedUser = useMemo(
    () => users.find((u) => u.userIdx === selectedIdx) || null,
    [users, selectedIdx]
  );

  // 팀 목록은 <회사 + 지점> 아래 단위라 지점이 정해져야 조회된다.
  // 저장된 지점이 아니라 '임시값의 지점'을 기준으로 본다 — 지점을 바꾸면
  // 팀 목록도 그 자리에서 따라 바뀌어야 하기 때문이다.
  const selectedDraftBranch = selectedUser
    ? (profileDraft[selectedUser.userIdx] || {}).branchName || ""
    : "";

  useEffect(() => {
    if (!selectedUser) {
      setTeamOptions([]);
      return;
    }
    loadTeamOptions(selectedUser.companyName, selectedDraftBranch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedDraftBranch]);

  useEffect(() => {
    setPage(1);
  }, [search]);
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  // 회사를 바꾸면 첫 페이지부터 — 3페이지를 보던 중에 바꾸면 빈 화면이 된다.
  useEffect(() => {
    setPage(1);
  }, [companyFilter]);
  useEffect(() => {
    if (filteredUsers.length === 0) {
      if (selectedIdx !== null) setSelectedIdx(null);
      return;
    }
    const visible = filteredUsers.some((u) => u.userIdx === selectedIdx);
    if (!visible) setSelectedIdx(filteredUsers[0].userIdx);
  }, [filteredUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEditPermissions = (targetUser) => {
    const targetRole = normalizeRole(targetUser?.roleCode);
    if (amISuperAdmin) return true;
    if (amIAdmin && targetRole === "USER") return true;
    return false;
  };
  // 비밀번호 초기화는 권한 수정과 조건이 다르다.
  //   본사(SUPER_ADMIN)는 전부, 회사관리자·회사총괄·지점관리자는 자기 소속 작업자 계정만.
  //   회사/지점 범위는 서버가 한 번 더 확인하므로 여기서는 역할만 본다.
  const canResetPassword = (targetUser) => {
    const targetRole = normalizeRole(targetUser?.roleCode);
    if (amISuperAdmin) return true;
    const managerRoles = ["ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"];
    if (managerRoles.includes(myRole) && ["USER", "WORKER"].includes(targetRole)) {
      return true;
    }
    return false;
  };

  // 소속·차량 수정은 권한 수정(canEditPermissions)과 조건이 다르다.
  //   canEditPermissions 는 민트워치 권한용이라 SUPER_ADMIN 과 'ADMIN→USER' 만 참이다.
  //   그걸 이 카드에 그대로 쓰는 바람에 본사관리자·지점관리자가 아예 못 고쳤고,
  //   대상이 WORKER 면 ADMIN 조차 막혔다(시험 계정 376개가 전부 WORKER).
  //
  //   비밀번호 초기화(canResetPassword)와 같은 기준으로 맞춘다 —
  //   회사/지점 범위는 서버(canAdminManageTarget)가 한 번 더 확인하므로 여기서는 역할만 본다.
  const canEditProfile = (targetUser) => {
    const targetRole = normalizeRole(targetUser?.roleCode);
    if (amISuperAdmin) return true;
    const managerRoles = ["ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"];
    if (managerRoles.includes(myRole) && ["USER", "WORKER"].includes(targetRole)) {
      return true;
    }
    return false;
  };

  /*
    관리자로 올릴 수 있는 대상: 일반 사용자(USER) + 작업자(WORKER).

    예전에는 USER 만 허용해서, 승인할 때 «설치팀장»으로 지정한 계정(WORKER)은
    관리자 지정 버튼이 아예 보이지 않았다. 서버는 원래 허용하고 있었으므로
    화면만 막혀 있던 셈이다(2026-10-13 수정).
  */
  const canShowPromoteButton = (targetUser) => {
    if (!amISuperAdmin) return false;
    const r = normalizeRole(targetUser?.roleCode);
    // 최고관리자 본인과 이미 관리자인 계정만 제외. 나머지는 전부 올릴 수 있다.
    return r !== "SUPER_ADMIN" && r !== "ADMIN";
  };
  const canShowDemoteButton = (targetUser) => {
    if (!amISuperAdmin) return false;
    const r = normalizeRole(targetUser?.roleCode);
    // 관리자 급(ADMIN·관제담당자·안전관리자)은 일반 사용자로 내릴 수 있다.
    return ["ADMIN", "HQ_MANAGER", "BRANCH_MANAGER"].includes(r);
  };

  // 민트워치 외 페이지 권한은 모두 동일하게 부여(true). 민트워치만 토글값 사용.
  const handleSaveMintwatch = async (targetUser) => {
    if (!canEditPermissions(targetUser)) {
      alert("이 계정의 권한은 수정할 수 없습니다.");
      return;
    }
    const userIdx = targetUser.userIdx;
    const watch = !!watchDraft[userIdx];
    setSavingUserIdx(userIdx);
    try {
      const res = await fetch("/api/admin/permissions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          userIdx,
          canDashboard: true,
          canLiveView: true,
          canPlayback: true,
          canSettings: true,
          canEvents: true,
          canMessages: watch,
          canDevices: true,
          canWorkers: true,
          canMap: true,
          canLogs: true,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "권한 저장 실패");
        return;
      }
      alert("저장 완료");
      loadUsers();
    } catch (err) {
      alert("권한 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingUserIdx(null);
    }
  };

  const changeRole = async (user, roleCode, confirmMsg, successMsg) => {
    if (!(await confirmDialog(confirmMsg, { title: "권한 변경" }))) return;
    setRoleSavingUserIdx(user.userIdx);
    try {
      const res = await fetch("/api/admin/role", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx: user.userIdx, roleCode }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "권한 변경 실패");
        return;
      }
      alert(successMsg);
      loadUsers();
    } catch (err) {
      alert("권한 변경 중 오류가 발생했습니다.");
    } finally {
      setRoleSavingUserIdx(null);
    }
  };

  const handleResetPassword = async (user) => {
    const ok = await confirmDialog(
      `[${user.userId}] 계정의 비밀번호를 초기화합니다.\n\n` +
        `비밀번호가 0 여덟 개(00000000)로 바뀌며, 기존 비밀번호는 사용할 수 없게 됩니다.\n` +
        `해당 사용자는 로그인 후 비밀번호를 변경해야만 다른 화면을 이용할 수 있습니다.`,
      { title: "비밀번호 초기화" }
    );
    if (!ok) return;

    setPwResetBusy(user.userIdx);
    setIssuedPassword(null);
    try {
      const res = await fetch("/api/admin/user/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ userIdx: user.userIdx }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.error || data.message || "비밀번호 초기화 실패");
        return;
      }
      setIssuedPassword({
        userIdx: user.userIdx,
        userId: data.userId,
        userName: data.userName,
        tempPassword: data.tempPassword,
      });
    } catch (err) {
      alert("비밀번호 초기화 중 오류가 발생했습니다.");
    } finally {
      setPwResetBusy(null);
    }
  };

  const handlePromoteAdmin = (user) =>
    changeRole(
      user,
      "ADMIN",
      `[${user.userId}] 계정을 관리자(ADMIN)로 지정하시겠습니까?`,
      "관리자 지정 완료"
    );
  const handleDemoteAdmin = (user) =>
    changeRole(
      user,
      "USER",
      `[${user.userId}] 계정의 관리자 권한을 회수하고 일반 사용자로 변경하시겠습니까?`,
      "관리자 회수 완료"
    );

  const RoleBadge = ({ roleCode }) => {
    const role = normalizeRole(roleCode);
    if (role === "SUPER_ADMIN")
      return (
        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
          S/A
        </span>
      );
    if (role === "ADMIN")
      return (
        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          관리자
        </span>
      );
    return (
      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
        사용자
      </span>
    );
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto pr-1">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">사용자 관리</h2>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            내 권한: <b>{amISuperAdmin ? "S/A" : myRole || "USER"}</b>
          </div>
        </div>

        {msg && (
          <div className="rounded-xl bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {msg}
          </div>
        )}

        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#1F2A3B]">
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
              불러오는 중...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:min-h-[460px] lg:grid-cols-12">
              {/* 좌측 목록 */}
              <div className="lg:col-span-4">
                {/* 회사 묶음 — 먼저 회사를 고르고 그 안에서 찾는다. */}
                <div className="mb-2 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCompanyFilter("")}
                    className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                      companyFilter === ""
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300"
                    }`}
                  >
                    전체 {users.length}
                  </button>
                  {companyGroups.map((g) => (
                    <button
                      key={g.name}
                      type="button"
                      onClick={() => setCompanyFilter(g.name)}
                      title={`${g.name} ${g.count}개`}
                      className={`max-w-[150px] truncate rounded-lg px-2.5 py-1 text-xs font-semibold transition ${
                        companyFilter === g.name
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300"
                      }`}
                    >
                      {g.name}{" "}
                      <span className={companyFilter === g.name ? "text-white/80" : "text-gray-400"}>
                        {g.count}
                      </span>
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    companyFilter
                      ? `${companyFilter} 안에서 검색 (아이디 / 이름)`
                      : "아이디 / 이름 / 회사 검색"
                  }
                  className="mb-3 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
                />

                <div className="space-y-1.5">
                  {filteredUsers.length === 0 ? (
                    <div className="px-2 py-6 text-center text-sm text-gray-500 dark:text-gray-300">
                      {search ? "검색 결과가 없습니다." : "사용자가 없습니다."}
                    </div>
                  ) : (
                    pagedUsers.map((u) => {
                      const active = u.userIdx === selectedIdx;
                      return (
                        <button
                          key={u.userIdx}
                          type="button"
                          onClick={() => setSelectedIdx(u.userIdx)}
                          className={`flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                            active
                              ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10"
                              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/5"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="line-clamp-1 text-sm font-semibold text-gray-900 dark:text-white">
                              {u.userName || "-"}
                            </div>
                            <div className="line-clamp-1 text-xs text-gray-500 dark:text-gray-400">
                              {u.userId}
                              {u.companyName ? ` · ${u.companyName}` : ""}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {/* 민트워치 배지 — 이 화면에서는 쓰지 않기로 해서 주석 처리(2026-09-23).

                            {u.canMessages && (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                워치
                              </span>
                            )}
                            */}
                            <RoleBadge roleCode={u.roleCode} />
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {filteredUsers.length > PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      ◀ 이전
                    </button>
                    <div className="min-w-[48px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {page} / {pageCount}
                    </div>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount}
                      className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200"
                    >
                      다음 ▶
                    </button>
                  </div>
                )}
              </div>

              {/* 우측 상세 */}
              <div className="lg:col-span-8">
                {!selectedUser ? (
                  <div className="flex h-full min-h-[260px] items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-gray-600">
                    왼쪽에서 사용자를 선택하세요.
                  </div>
                ) : (
                  <UserDetail
                    user={selectedUser}
                    editable={canEditProfile(selectedUser)}
                    saving={savingUserIdx === selectedUser.userIdx}
                    roleBusy={roleSavingUserIdx === selectedUser.userIdx}
                    showPromote={canShowPromoteButton(selectedUser)}
                    showDemote={canShowDemoteButton(selectedUser)}
                    onPromote={() => handlePromoteAdmin(selectedUser)}
                    onDemote={() => handleDemoteAdmin(selectedUser)}
                    amIAdmin={amIAdmin}
                    canReset={canResetPassword(selectedUser)}
                    pwResetBusy={pwResetBusy === selectedUser.userIdx}
                    onResetPassword={() => handleResetPassword(selectedUser)}
                    issuedPassword={
                      issuedPassword && issuedPassword.userIdx === selectedUser.userIdx
                        ? issuedPassword
                        : null
                    }
                    onCloseIssuedPassword={() => setIssuedPassword(null)}
                    profile={profileDraft[selectedUser.userIdx] || {}}
                    onProfileChange={(field, value) =>
                      setProfileField(selectedUser.userIdx, field, value)
                    }
                    onSaveProfile={() => handleSaveProfile(selectedUser)}
                    profileSaving={profileBusy === selectedUser.userIdx}
                    branchOptions={branchOptions.filter(
                      (b) => b.companyName === selectedUser.companyName
                    )}
                    teamOptions={teamOptions}
                    canEditBranch={
                      // 지점 변경 제한을 두지 않기로 했다(2026-09-23 요청).
                      // 본사관리자·지점관리자도 지점을 바꿀 수 있고, 서버도 같이 풀었다.
                      // 회사·지점 격리는 canAdminManageTarget 이 이미 하고 있어
                      // 남의 회사 계정은 애초에 이 화면에 뜨지 않는다.
                      true
                    }
                  />
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function UserDetail({
  user,
  editable,
  // 민트워치 — 이 화면에서는 쓰지 않기로 해서 주석 처리(2026-09-23).
  // watch,
  // onWatchChange,
  // onSaveWatch,
  saving,
  roleBusy,
  showPromote,
  showDemote,
  onPromote,
  onDemote,
  amIAdmin,
  canReset,
  pwResetBusy,
  onResetPassword,
  issuedPassword,
  onCloseIssuedPassword,
  // 가입 이후 프로필(지점·팀·차량번호) 수정
  profile,
  onProfileChange,
  onSaveProfile,
  profileSaving,
  branchOptions,
  teamOptions,
  canEditBranch,
}) {
  const role = normalizeRole(user.roleCode);
  const approved = Number(user.approvalStatus) === 1;

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700 lg:h-full">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-gray-900 dark:text-white">
              {user.userName || "-"}
            </span>
            <RoleBadgeInline roleCode={user.roleCode} />
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{user.userId}</div>
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${
            approved
              ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300"
              : "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-300"
          }`}
        >
          {approved ? "승인됨" : "대기/차단"}
        </span>
      </div>

      {/* 필드가 7개라 2단이면 4행을 먹는다. 넓은 화면에서 4단으로 세워 2행으로 줄인다.
          좁은 화면은 2단을 유지한다 — 4단으로 두면 값이 뭉개진다. */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 lg:grid-cols-4">
        <Field label="회사명" value={user.companyName || "-"} />
        <Field label="지점" value={user.branchName || "-"} />
        <Field label="팀" value={user.teamName || "-"} />
        <Field label="차량번호" value={user.vehicleNo || "-"} />
        <Field label="전화번호" value={maskPhone(user.phoneMid, user.phoneLast)} />
        <Field label="UserIdx" value={String(user.userIdx)} />
        <Field label="PathKey" value={user.userPathKey || "-"} />
      </div>

      {/* 소속·차량 수정 — 가입할 때 한 번 받고 끝이라 고칠 방법이 없었다.
          지점·팀은 등록된 것 중에서만 고르게 한다(자유 입력을 받으면 가입
          드롭다운과 어긋나 그 계정이 목록에서 고를 수 없는 상태가 된다). */}
      <div className="mt-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <div className="text-sm font-bold text-gray-900 dark:text-white">
          소속 · 차량 수정
        </div>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          화면 곳곳에 «지점 / 팀 / 이름 / 차량번호» 로 표시되는 값입니다.
        </div>

        {/* 세로로 쌓으면 3행을 먹어 상세가 한 화면을 넘긴다.
            가로 3단으로 세워 1행으로 줄인다(내용은 그대로).
            좁은 화면에서는 자동으로 다시 세로로 쌓인다. */}
        <div className="mt-3 grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              지점
            </label>
            {editable && canEditBranch ? (
              <select
                value={(profile || {}).branchName || ""}
                onChange={(e) => onProfileChange("branchName", e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
              >
                <option value="">지점 없음</option>
                {(branchOptions || []).map((b) => (
                  <option key={b.branchIdx} value={b.branchName}>
                    {b.branchName}
                  </option>
                ))}
              </select>
            ) : (
              <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-white/5 dark:text-gray-200">
                {(profile || {}).branchName || "-"}
                <span className="ml-2 text-[11px] text-gray-400">
                  (지점 변경은 회사 전체 관리자만)
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              팀
            </label>
            <select
              value={(profile || {}).teamName || ""}
              disabled={!editable}
              onChange={(e) => onProfileChange("teamName", e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
            >
              <option value="">팀 없음</option>
              {(teamOptions || []).map((t) => (
                <option key={t.teamIdx} value={t.teamName}>
                  {t.teamName}
                </option>
              ))}
              {/* 지금 값이 목록에 없으면(지점을 바꿨거나 팀이 삭제된 경우)
                  선택이 조용히 비어 보이므로 항목으로 끼워 넣어 드러낸다. */}
              {(profile || {}).teamName &&
              !(teamOptions || []).some((t) => t.teamName === profile.teamName) ? (
                <option value={profile.teamName}>
                  {profile.teamName} (등록되지 않은 팀)
                </option>
              ) : null}
            </select>
            {(branchOptions || []).length > 0 && !(profile || {}).branchName ? (
              <div className="mt-1 text-[11px] text-gray-400">
                지점을 먼저 정해야 팀을 고를 수 있습니다.
              </div>
            ) : null}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
              차량번호
            </label>
            <input
              type="text"
              value={(profile || {}).vehicleNo || ""}
              disabled={!editable}
              maxLength={30}
              onChange={(e) => onProfileChange("vehicleNo", e.target.value)}
              placeholder="차량번호"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
            />
          </div>
        </div>

        {editable ? (
          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={onSaveProfile}
              disabled={profileSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {profileSaving ? "저장 중..." : "저장"}
            </button>
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            이 계정은 현재 로그인 권한으로 수정할 수 없습니다.
          </div>
        )}
      </div>

      {/* 민트워치 사용 권한 — 이 화면에서는 쓰지 않기로 해서 통째로 주석 처리(2026-09-23).
          되살릴 때는 이 블록과 함께 UserDetail 시그니처의 watch/onWatchChange/onSaveWatch,
          호출부의 같은 props, 부모의 watchDraft·handleSaveMintwatch·목록 배지를 같이 푼다.

      <div className="mt-5 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">
              민트워치 사용 권한
            </div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              끄면 이 계정에서 민트워치(메시지) 기능이 숨겨집니다. 그 외 화면은 모두 동일하게 제공됩니다.
            </div>
          </div>
          <label className="inline-flex shrink-0 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={watch}
              disabled={!editable}
              onChange={(e) => onWatchChange(e.target.checked)}
              className="h-5 w-5 accent-emerald-600"
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {watch ? "사용" : "미사용"}
            </span>
          </label>
        </div>

        {editable ? (
          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={onSaveWatch}
              disabled={saving}
              className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
            >
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        ) : (
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            이 계정의 권한은 현재 로그인 권한으로 수정할 수 없습니다.
          </div>
        )}
      </div>
      */}

      {/* 관리자 지정 / 회수 */}
      <div className="mt-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">관리자 권한</div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              관리자는 사용자/승인/카메라 관리에 접근할 수 있습니다.
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            {showPromote && (
              <button
                type="button"
                onClick={onPromote}
                disabled={roleBusy}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {roleBusy ? "처리 중..." : "관리자 지정"}
              </button>
            )}
            {showDemote && (
              <button
                type="button"
                onClick={onDemote}
                disabled={roleBusy}
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {roleBusy ? "처리 중..." : "관리자 회수"}
              </button>
            )}
            {role === "SUPER_ADMIN" && (
              <span className="rounded-lg bg-purple-100 px-4 py-2 text-sm font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                S/A 계정
              </span>
            )}
            {amIAdmin && role !== "USER" && (
              <span className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                읽기 전용
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 비밀번호 초기화 */}
      {canReset && (
        <div className="mt-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">비밀번호 초기화</div>
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                비밀번호를 잊은 사용자에게 임시 비밀번호를 발급합니다.
              </div>
            </div>
            <button
              type="button"
              onClick={onResetPassword}
              disabled={pwResetBusy}
              className="shrink-0 rounded-lg bg-slate-600 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
            >
              {pwResetBusy ? "발급 중..." : "초기화"}
            </button>
          </div>

          {issuedPassword && (
            <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
              <div className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                비밀번호가 초기화되었습니다
              </div>
              <div className="mt-2 select-all break-all font-mono text-2xl font-bold tracking-wider text-gray-900 dark:text-white">
                {issuedPassword.tempPassword}
              </div>
              <div className="mt-2 text-xs text-amber-800 dark:text-amber-300">
                {issuedPassword.userName}({issuedPassword.userId}) 님에게 전달해주세요. 로그인하면
                비밀번호 변경 화면이 뜨며, 변경하기 전에는 다른 화면을 이용할 수 없습니다.
              </div>
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={onCloseIssuedPassword}
                  className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                >
                  확인했습니다
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-[#16202C]">
      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 break-all text-sm text-gray-800 dark:text-gray-100">{value}</div>
    </div>
  );
}

function RoleBadgeInline({ roleCode }) {
  const role = normalizeRole(roleCode);
  if (role === "SUPER_ADMIN")
    return (
      <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
        S/A
      </span>
    );
  if (role === "ADMIN")
    return (
      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        관리자
      </span>
    );
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      사용자
    </span>
  );
}
